import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  MenuItem,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  AlertTitle,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Divider,
  Chip,
} from '@mui/material';
import {
  ArrowBack,
  Edit as EditIcon,
  PersonAddAlt,
  SwapHoriz,
  DeleteForever,
  Build,
  Inventory2,
  CalendarMonth,
  Shield,
  AccountTree,
  Add,
  CheckCircle,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
} from 'recharts';
import {
  PageHeader,
  StatCard,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  TableEmptyState,
} from '../../components/common/pageKit';
import { useChartTokens, axisProps, gridProps, tooltipStyle } from '../../dashboard/chartTokens';
import { useAuth } from '../../context/AuthContext';
import assetService, {
  AssetDetail,
  AssetCategory,
  AssetBlock,
  AssetStatus,
  AssetMaintenance,
  DepreciationSchedule,
  ASSET_STATUSES,
  MAINTENANCE_TYPES,
  MaintenanceType,
  DisposalType,
  DISPOSAL_TYPES,
} from '../../services/assetService';
import hrService, { EmployeeListItem } from '../../services/hrService';
import {
  fmtMoney,
  fmtDate,
  fmtNum,
  toNum,
  netBookValue,
  assigneeName,
  ASSET_STATUS_LABEL,
  ASSET_STATUS_TONE,
  DEPRECIATION_METHOD_LABEL,
  MAINTENANCE_TYPE_LABEL,
  DISPOSAL_TYPE_LABEL,
  RUN_STATUS_LABEL,
  RUN_STATUS_TONE,
} from './assetFormat';
import AssetFormDialog from './AssetFormDialog';

const FINANCE_ROLES = ['SUPER_ADMIN', 'ADMIN', 'FINANCE'];

// ---------------------------------------------------------------------------
// Info card — label/value pairs, not a KPI. Reused for the four fact panels.
// ---------------------------------------------------------------------------

const InfoCard: React.FC<{ title: string; icon: React.ReactNode; rows: { label: string; value: React.ReactNode }[] }> = ({
  title,
  icon,
  rows,
}) => (
  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.75 }}>
      <Box sx={{ color: 'text.secondary', display: 'flex', '& svg': { fontSize: 18 } }}>{icon}</Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1 }}>
      {rows.map((r) => (
        <Box key={r.label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ pt: 0.15 }}>
            {r.label}
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
          >
            {r.value}
          </Typography>
        </Box>
      ))}
    </Box>
  </Paper>
);

// ---------------------------------------------------------------------------
// Assign dialog
// ---------------------------------------------------------------------------

const AssignDialog: React.FC<{
  open: boolean;
  asset: AssetDetail | null;
  employees: EmployeeListItem[];
  onClose: () => void;
  onSaved: () => void;
}> = ({ open, asset, employees, onClose, onSaved }) => {
  const [selected, setSelected] = useState<EmployeeListItem | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && asset) {
      const current = employees.find((e) => e.id === asset.assigned_to_employee_id) || null;
      setSelected(current);
      setError(null);
    }
  }, [open, asset, employees]);

  if (!asset) return null;

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      await assetService.assignAsset(asset.id, selected ? selected.id : null);
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update assignment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PersonAddAlt color="primary" />
        Assign Asset
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Autocomplete
          options={employees}
          value={selected}
          onChange={(_, v) => setSelected(v)}
          getOptionLabel={(o) => [o.first_name, o.last_name].filter(Boolean).join(' ')}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          renderInput={(params) => <TextField {...params} label="Employee" size="small" sx={{ mt: 1 }} />}
          disabled={saving}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Clear the field to unassign. Assigning an employee sets the asset's status to In Use.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Status dialog
// ---------------------------------------------------------------------------

const StatusDialog: React.FC<{
  open: boolean;
  asset: AssetDetail | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ open, asset, onClose, onSaved }) => {
  const [status, setStatus] = useState<AssetStatus>('IN_STORE');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && asset) {
      setStatus(asset.status);
      setError(null);
    }
  }, [open, asset]);

  if (!asset) return null;

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      await assetService.updateAssetStatus(asset.id, status);
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SwapHoriz color="primary" />
        Change Status
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <TextField
          select
          fullWidth
          label="Status"
          size="small"
          sx={{ mt: 1 }}
          value={status}
          onChange={(e) => setStatus(e.target.value as AssetStatus)}
          disabled={saving}
        >
          {ASSET_STATUSES.filter((s) => s !== 'DISPOSED').map((s) => (
            <MenuItem key={s} value={s}>
              {ASSET_STATUS_LABEL[s]}
            </MenuItem>
          ))}
        </TextField>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          To take this asset off the books entirely, use Dispose instead — it is not a status.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving || status === asset.status}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Dispose dialog — financial-close action, deliberately heavy.
// ---------------------------------------------------------------------------

const DisposeDialog: React.FC<{
  open: boolean;
  asset: AssetDetail | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ open, asset, onClose, onSaved }) => {
  const [disposalDate, setDisposalDate] = useState(new Date().toISOString().slice(0, 10));
  const [disposalType, setDisposalType] = useState<DisposalType>('SALE');
  const [proceeds, setProceeds] = useState('0');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDisposalDate(new Date().toISOString().slice(0, 10));
      setDisposalType('SALE');
      setProceeds('0');
      setNotes('');
      setError(null);
    }
  }, [open]);

  if (!asset) return null;

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      await assetService.disposeAsset(asset.id, {
        disposalDate,
        disposalType,
        proceeds: proceeds ? Number(proceeds) : 0,
        notes: notes || undefined,
      });
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to dispose asset');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DeleteForever color="error" />
        Dispose Asset
      </DialogTitle>
      <DialogContent>
        <Alert severity="error" sx={{ mb: 2.5 }}>
          <AlertTitle>This takes the asset off the books</AlertTitle>
          Depreciation is caught up to the disposal date, the gain or loss on disposal is computed, and a journal
          entry is posted immediately. This cannot be undone from here.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Disposal Date"
            type="date"
            size="small"
            fullWidth
            value={disposalDate}
            onChange={(e) => setDisposalDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            disabled={saving}
          />
          <TextField
            select
            label="Disposal Type"
            size="small"
            fullWidth
            value={disposalType}
            onChange={(e) => setDisposalType(e.target.value as DisposalType)}
            disabled={saving}
          >
            {DISPOSAL_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {DISPOSAL_TYPE_LABEL[t]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Proceeds"
            type="number"
            size="small"
            fullWidth
            value={proceeds}
            onChange={(e) => setProceeds(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
            disabled={saving}
          />
          <TextField
            label="Notes"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={saving}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" color="error" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Disposing…' : 'Dispose Asset'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Maintenance dialogs — schedule new / mark complete.
// ---------------------------------------------------------------------------

const ScheduleMaintenanceDialog: React.FC<{
  open: boolean;
  assetId: number;
  onClose: () => void;
  onSaved: () => void;
}> = ({ open, assetId, onClose, onSaved }) => {
  const [maintenanceType, setMaintenanceType] = useState<MaintenanceType>('PREVENTIVE');
  const [scheduledDate, setScheduledDate] = useState('');
  const [cost, setCost] = useState('0');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setMaintenanceType('PREVENTIVE');
      setScheduledDate('');
      setCost('0');
      setNotes('');
      setError(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      await assetService.createMaintenance({
        assetId,
        maintenanceType,
        scheduledDate: scheduledDate || undefined,
        cost: cost ? Number(cost) : undefined,
        notes: notes || undefined,
      });
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to schedule maintenance');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Build color="primary" />
        Schedule Maintenance
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            select
            label="Type"
            size="small"
            fullWidth
            value={maintenanceType}
            onChange={(e) => setMaintenanceType(e.target.value as MaintenanceType)}
            disabled={saving}
          >
            {MAINTENANCE_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {MAINTENANCE_TYPE_LABEL[t]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Scheduled Date"
            type="date"
            size="small"
            fullWidth
            value={scheduledDate}
            onChange={(e) => setScheduledDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            disabled={saving}
          />
          <TextField
            label="Estimated Cost"
            type="number"
            size="small"
            fullWidth
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
            disabled={saving}
          />
          <TextField
            label="Notes"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={saving}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Scheduling…' : 'Schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CompleteMaintenanceDialog: React.FC<{
  target: AssetMaintenance | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ target, onClose, onSaved }) => {
  const [completedDate, setCompletedDate] = useState(new Date().toISOString().slice(0, 10));
  const [cost, setCost] = useState('0');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (target) {
      setCompletedDate(new Date().toISOString().slice(0, 10));
      setCost(target.cost !== null && target.cost !== undefined ? String(target.cost) : '0');
      setNotes(target.notes || '');
      setError(null);
    }
  }, [target]);

  if (!target) return null;

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      await assetService.completeMaintenance(target.id, {
        completedDate,
        cost: cost ? Number(cost) : undefined,
        notes: notes || undefined,
      });
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to mark maintenance complete');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!target} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CheckCircle color="success" />
        Mark Maintenance Complete
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Completed Date"
            type="date"
            size="small"
            fullWidth
            value={completedDate}
            onChange={(e) => setCompletedDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            disabled={saving}
          />
          <TextField
            label="Actual Cost"
            type="number"
            size="small"
            fullWidth
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
            disabled={saving}
          />
          <TextField
            label="Notes"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={saving}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" color="success" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Saving…' : 'Mark Complete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Depreciation schedule chart — Companies Act NBV over time.
//
// Income Tax depreciation has no per-asset schedule to show: that book
// depreciates a whole BLOCK of assets together (see asset_block_balances /
// the IT Blocks page), not this one asset, so there is no second series to
// plot here even on a "dual book" module — this chart is Companies Act only,
// and says so.
// ---------------------------------------------------------------------------

const ScheduleChart: React.FC<{ schedule: DepreciationSchedule }> = ({ schedule }) => {
  const t = useChartTokens();
  const entries = [...schedule.entries].sort((a, b) => a.period_start.localeCompare(b.period_start));

  const data = entries.map((e) => ({
    period: fmtDate(e.period_end),
    openingWdv: toNum(e.opening_wdv) ?? 0,
    charge: toNum(e.depreciation_amount) ?? 0,
    closingWdv: toNum(e.closing_wdv) ?? 0,
  }));

  return (
    <Box sx={{ height: 280, width: '100%' }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid {...gridProps(t)} />
          <XAxis dataKey="period" {...axisProps(t)} interval="preserveStartEnd" minTickGap={24} />
          <YAxis {...axisProps(t)} width={64} tickFormatter={(v: number) => fmtNum(v)} />
          <RTooltip {...tooltipStyle(t)} formatter={(v: any) => [fmtMoney(Number(v)), 'Closing WDV']} />
          <Line
            type="monotone"
            dataKey="closingWdv"
            name="Closing WDV"
            stroke={t.series(0)}
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5, strokeWidth: 2, stroke: t.surface }}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const AssetDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [asset, setAsset] = useState<AssetDetail | null>(null);
  const [schedule, setSchedule] = useState<DepreciationSchedule | null>(null);
  const [loading, setLoading] = useState(true);
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const [categories, setCategories] = useState<AssetCategory[]>([]);
  const [blocks, setBlocks] = useState<AssetBlock[]>([]);
  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);

  const [editOpen, setEditOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [disposeOpen, setDisposeOpen] = useState(false);
  const [scheduleMaintOpen, setScheduleMaintOpen] = useState(false);
  const [completeTarget, setCompleteTarget] = useState<AssetMaintenance | null>(null);

  const canDispose = Boolean(user?.role && FINANCE_ROLES.includes(user.role));

  const fetchAsset = useCallback(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    assetService
      .getAsset(id)
      .then((r) => setAsset(r.asset))
      .catch((err: any) => setError(err.message || 'Failed to load asset'))
      .finally(() => setLoading(false));
  }, [id]);

  const fetchSchedule = useCallback(() => {
    if (!id) return;
    setScheduleLoading(true);
    assetService
      .getDepreciationSchedule(id)
      .then(setSchedule)
      .catch(() => {
        // Chart/table fall back to the empty state — never fabricate entries.
        setSchedule(null);
      })
      .finally(() => setScheduleLoading(false));
  }, [id]);

  useEffect(() => {
    fetchAsset();
    fetchSchedule();
  }, [fetchAsset, fetchSchedule]);

  useEffect(() => {
    assetService.getCategories().then((r) => setCategories(r.categories)).catch(() => {});
    assetService.getBlocks().then((r) => setBlocks(r.blocks)).catch(() => {});
    hrService.getEmployees({ limit: 200 }).then((r) => setEmployees(r.employees)).catch(() => {});
  }, []);

  const refreshAfterMutation = (message: string) => {
    setToast({ message, severity: 'success' });
    fetchAsset();
    fetchSchedule();
  };

  const blockName = useMemo(() => {
    if (!asset?.it_block_id) return null;
    return blocks.find((b) => b.id === asset.it_block_id)?.block_name || null;
  }, [asset, blocks]);

  const warrantyState = useMemo(() => {
    if (!asset?.warranty_until) return null;
    const until = new Date(asset.warranty_until);
    if (Number.isNaN(until.getTime())) return null;
    const days = Math.ceil((until.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    if (days < 0) return { label: 'Expired', tone: 'error' as const };
    if (days <= 30) return { label: `Expires in ${days}d`, tone: 'warning' as const };
    return { label: 'Active', tone: 'success' as const };
  }, [asset]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !asset) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/assets')} sx={{ mb: 2 }}>
          Back to Register
        </Button>
        <Alert severity="error">{error || 'Asset not found'}</Alert>
      </Box>
    );
  }

  const nbv = netBookValue(asset.gross_block, asset.accumulated_depreciation);
  const assignee = assigneeName(asset.assignee_first_name, asset.assignee_last_name);
  const noDepReason = asset.depreciation_method === 'NONE'
    ? 'This asset is configured not to depreciate.'
    : !asset.put_to_use_on
    ? 'This asset has no put-to-use date, so it cannot depreciate yet.'
    : 'No depreciation run has posted a charge for this asset yet.';

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/assets')} sx={{ mb: 1.5 }} size="small">
        Back to Register
      </Button>

      <PageHeader
        title={asset.name}
        qualifier={`— ${asset.asset_code}`}
        subtitle={[asset.category_name, asset.location].filter(Boolean).join(' · ') || undefined}
        actions={
          <>
            <StatusDot label={ASSET_STATUS_LABEL[asset.status]} tone={ASSET_STATUS_TONE[asset.status]} />
            {asset.status !== 'DISPOSED' && (
              <>
                <Button size="small" startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>
                  Edit
                </Button>
                <Button size="small" startIcon={<PersonAddAlt />} onClick={() => setAssignOpen(true)}>
                  Assign
                </Button>
                <Button size="small" startIcon={<SwapHoriz />} onClick={() => setStatusOpen(true)}>
                  Status
                </Button>
                {canDispose && (
                  <Button size="small" color="error" startIcon={<DeleteForever />} onClick={() => setDisposeOpen(true)}>
                    Dispose
                  </Button>
                )}
              </>
            )}
          </>
        }
      />

      {asset.status === 'DISPOSED' && asset.disposed_on && (
        <Alert severity="info" sx={{ mb: 2.5 }}>
          This asset was disposed on {fmtDate(asset.disposed_on)}. It is off the books; no further actions apply.
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard title="Gross Block" value={fmtMoney(asset.gross_block)} icon={<Inventory2 />} color="#0284c7" />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard title="Accum. Depreciation" value={fmtMoney(asset.accumulated_depreciation)} icon={<AccountTree />} color="#d97706" />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard title="Net Book Value" value={nbv === null ? null : fmtMoney(nbv)} icon={<AccountTree />} color="#16a34a" />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard title="Current Value" value={fmtMoney(asset.current_value)} icon={<Inventory2 />} color="#7c3aed" />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <InfoCard
            title="Acquisition"
            icon={<CalendarMonth />}
            rows={[
              { label: 'Purchase Date', value: fmtDate(asset.purchase_date) },
              { label: 'Put to Use On', value: fmtDate(asset.put_to_use_on) },
              { label: 'Gross Block', value: fmtMoney(asset.gross_block) },
              { label: 'Salvage Value', value: fmtMoney(asset.salvage_value) },
            ]}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <InfoCard
            title="Depreciation Config"
            icon={<AccountTree />}
            rows={[
              { label: 'Method', value: asset.depreciation_method ? DEPRECIATION_METHOD_LABEL[asset.depreciation_method] : 'Category default' },
              { label: 'Useful Life', value: asset.useful_life_months ? `${asset.useful_life_months} months` : '—' },
              { label: 'IT Block', value: blockName || (asset.it_block_id ? `#${asset.it_block_id}` : 'Category default') },
              { label: 'Serial Number', value: asset.serial_number || '—' },
            ]}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <InfoCard
            title="Assignment"
            icon={<PersonAddAlt />}
            rows={[
              { label: 'Assigned To', value: assignee || 'Unassigned' },
              { label: 'Status', value: <StatusDot label={ASSET_STATUS_LABEL[asset.status]} tone={ASSET_STATUS_TONE[asset.status]} /> },
              { label: 'Location', value: asset.location || '—' },
              { label: 'Category', value: asset.category_name || 'Uncategorised' },
            ]}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <InfoCard
            title="Warranty"
            icon={<Shield />}
            rows={[
              { label: 'Warranty Until', value: fmtDate(asset.warranty_until) },
              {
                label: 'Coverage',
                value: warrantyState ? (
                  <StatusDot label={warrantyState.label} tone={warrantyState.tone} />
                ) : (
                  '—'
                ),
              },
              { label: 'Capitalised On', value: fmtDate(asset.capitalised_on) },
              { label: 'CWIP', value: asset.is_cwip ? 'Yes' : 'No' },
            ]}
          />
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Depreciation Schedule — Companies Act
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Closing WDV after each posted run. Income Tax depreciation is computed per block of assets, not per
              asset — see IT Blocks for this asset's block position.
            </Typography>
          </Box>
        </Box>

        {scheduleLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={26} />
          </Box>
        ) : !schedule || schedule.entries.length === 0 ? (
          <TableEmptyState title="No depreciation posted yet" hint={noDepReason} />
        ) : (
          <>
            <ScheduleChart schedule={schedule} />
            <TableContainer sx={{ overflowX: 'auto', mt: 2 }}>
              <Table size="small" sx={{ minWidth: 640 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Period</TableCell>
                    <TableCell sx={headerCellSx} align="right">Opening WDV</TableCell>
                    <TableCell sx={headerCellSx} align="right">Charge</TableCell>
                    <TableCell sx={headerCellSx} align="right">Closing WDV</TableCell>
                    <TableCell sx={headerCellSx}>Method</TableCell>
                    <TableCell sx={headerCellSx}>Run Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...schedule.entries]
                    .sort((a, b) => b.period_start.localeCompare(a.period_start))
                    .map((e) => (
                      <TableRow key={e.id} sx={bodyRowSx}>
                        <TableCell>
                          {fmtDate(e.period_start)} – {fmtDate(e.period_end)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(e.opening_wdv)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(e.depreciation_amount)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmtMoney(e.closing_wdv)}</TableCell>
                        <TableCell>{e.method}</TableCell>
                        <TableCell>
                          <StatusDot label={RUN_STATUS_LABEL[e.run_status]} tone={RUN_STATUS_TONE[e.run_status]} />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2.5, pb: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Maintenance History
          </Typography>
          <Button size="small" startIcon={<Add />} onClick={() => setScheduleMaintOpen(true)}>
            Schedule Maintenance
          </Button>
        </Box>
        <Divider />
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Type</TableCell>
                <TableCell sx={headerCellSx}>Scheduled</TableCell>
                <TableCell sx={headerCellSx}>Completed</TableCell>
                <TableCell sx={headerCellSx} align="right">Cost</TableCell>
                <TableCell sx={headerCellSx}>Notes</TableCell>
                <TableCell sx={headerCellSx} align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {asset.maintenanceHistory.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ border: 0 }}>
                    <TableEmptyState title="No maintenance recorded" hint="Schedule the first maintenance for this asset above." />
                  </TableCell>
                </TableRow>
              ) : (
                asset.maintenanceHistory.map((m) => (
                  <TableRow key={m.id} sx={bodyRowSx}>
                    <TableCell>{MAINTENANCE_TYPE_LABEL[m.maintenance_type]}</TableCell>
                    <TableCell>{fmtDate(m.scheduled_date)}</TableCell>
                    <TableCell>
                      {m.completed_date ? (
                        fmtDate(m.completed_date)
                      ) : (
                        <Chip size="small" label="Pending" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(m.cost)}</TableCell>
                    <TableCell sx={{ maxWidth: 240 }}>
                      <Typography variant="caption" color="text.secondary">
                        {m.notes || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {!m.completed_date && (
                        <Button size="small" onClick={() => setCompleteTarget(m)}>
                          Mark Complete
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <AssetFormDialog
        open={editOpen}
        mode="edit"
        asset={asset}
        categories={categories}
        blocks={blocks}
        onClose={() => setEditOpen(false)}
        onSaved={() => refreshAfterMutation('Asset updated')}
      />
      <AssignDialog
        open={assignOpen}
        asset={asset}
        employees={employees}
        onClose={() => setAssignOpen(false)}
        onSaved={() => refreshAfterMutation('Assignment updated')}
      />
      <StatusDialog
        open={statusOpen}
        asset={asset}
        onClose={() => setStatusOpen(false)}
        onSaved={() => refreshAfterMutation('Status updated')}
      />
      <DisposeDialog
        open={disposeOpen}
        asset={asset}
        onClose={() => setDisposeOpen(false)}
        onSaved={() => refreshAfterMutation('Asset disposed')}
      />
      <ScheduleMaintenanceDialog
        open={scheduleMaintOpen}
        assetId={asset.id}
        onClose={() => setScheduleMaintOpen(false)}
        onSaved={() => refreshAfterMutation('Maintenance scheduled')}
      />
      <CompleteMaintenanceDialog
        target={completeTarget}
        onClose={() => setCompleteTarget(null)}
        onSaved={() => refreshAfterMutation('Maintenance marked complete')}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default AssetDetailPage;
